import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import jsQR from 'jsqr';

// Tar imot en QR-kode fra kameraet og kaller onScan med koden. Godtar både
// ren kode («7», «B12») og Toolshare-lenken (app.toolshare.no/?code=7) som
// lages av QR-ikonet på lister og ting. Samme sanering som ved innlasting av
// ?code=-lenken i App.tsx, så det som sendes videre er trygt å slå opp.
export const extractItemCode = (text: string): string => {
  let raw = text.trim();
  try {
    const url = new URL(raw);
    const param = url.searchParams.get('code');
    if (param) raw = param;
  } catch {
    // ikke en URL — bruk teksten direkte
  }
  return raw.trim().toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 12);
};

export const CodeScanner: React.FC<{ onScan: (code: string) => void; onClose: () => void }> = ({ onScan, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const [camError, setCamError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let raf = 0;
    let stream: MediaStream | null = null;

    const stop = () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach(t => t.stop());
    };

    navigator.mediaDevices?.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then(s => {
        if (cancelled) { s.getTracks().forEach(t => t.stop()); return; }
        stream = s;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) { stop(); return; }
        video.srcObject = s;
        video.play().catch(() => {});
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) { stop(); setCamError('Kunne ikke lese bildet fra kameraet.'); return; }

        const tick = () => {
          if (cancelled) return;
          if (video.readyState >= video.HAVE_ENOUGH_DATA && video.videoWidth > 0) {
            // Skalerer ned før dekoding — raskere, og QR-koder trenger ikke full oppløsning.
            const scale = Math.min(1, 640 / video.videoWidth);
            canvas.width = Math.round(video.videoWidth * scale);
            canvas.height = Math.round(video.videoHeight * scale);
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const result = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
            if (result) {
              const code = extractItemCode(result.data);
              if (code) {
                stop();
                onScanRef.current(code);
                return;
              }
            }
          }
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      })
      .catch(err => {
        if (cancelled) return;
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setCamError('Nettleseren har ikke tilgang til kameraet.\n\nGå til telefonens Innstillinger → Personvern → Kamera, og slå på tilgang for nettleseren din (Edge, Chrome, osv.).');
        } else if (err.name === 'NotFoundError') {
          setCamError('Ingen kamera funnet på enheten.');
        } else {
          setCamError('Kunne ikke åpne kameraet. Prøv å skrive inn koden manuelt i stedet.');
        }
      });

    return stop;
  }, []);

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 2000, display: 'flex', flexDirection: 'column' }}>
      {camError ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center', gap: 16 }}>
          <div style={{ fontSize: 48 }}>📷</div>
          <div style={{ color: '#fff', fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{camError}</div>
          <button onClick={onClose} style={{ marginTop: 8, padding: '12px 28px', borderRadius: 10, border: 'none', background: '#fff', color: '#000', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Lukk</button>
        </div>
      ) : (
        <>
          <video ref={videoRef} playsInline muted style={{ flex: 1, width: '100%', objectFit: 'cover', minHeight: 0 }} />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 240, height: 240, border: '3px solid rgba(255,255,255,0.9)', borderRadius: 16, boxShadow: '0 0 0 100vmax rgba(0,0,0,0.45)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: 24, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: 15, fontWeight: 500, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
            Hold kameraet mot QR-koden
          </div>
          <button onClick={onClose} aria-label="Lukk"
            style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', padding: '12px 28px', borderRadius: 10, border: 'none', background: '#fff', color: '#000', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
            Avbryt
          </button>
        </>
      )}
    </div>,
    document.body,
  );
};
